// Screener-sida: filterpanel + sorterbar tabell. State bor i URL:en så
// länkar är delbara. Går direkt till PocketBase (samma som watchlist),
// utan att gå genom backend/nginx.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterPanel from '../components/screener/FilterPanel';
import ScreenerTable from '../components/screener/ScreenerTable';
import { pbBaseUrl } from '../lib/pocketbase';
import {
  type ScreenerState,
  SCREENER_DEFAULTS,
  buildPbFilter,
  buildPbSort,
  stateFromSearchParams,
  stateToSearchParams,
} from '../lib/screener';
import type { Stock } from '../types/stock';

export default function Screener() {
  const [searchParams, setSearchParams] = useSearchParams();
  const state: ScreenerState = stateFromSearchParams(searchParams);

  const [stocks, setStocks] = useState<Stock[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce endast query-parametrar, inte UI-state som page.
  const queryKey = JSON.stringify({
    q: state.q, pe: state.pe, div: state.div, mcap: state.mcap, rsi: state.rsi,
    sector: state.sector, country: state.country, sort: state.sort, page: state.page, perPage: state.perPage,
  });
  const debouncedKey = useDebouncedKey(queryKey, 250);
  const firstRender = useRef(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const debouncedState: ScreenerState = JSON.parse(debouncedKey);

    // Bygg PocketBase-filter och sort — går direkt till PB, samma som watchlist.
    const filter = buildPbFilter(debouncedState);
    const sort = buildPbSort(debouncedState);

    const params = new URLSearchParams();
    params.set('page', String(debouncedState.page));
    params.set('perPage', String(debouncedState.perPage));
    if (filter) params.set('filter', filter);
    if (sort) params.set('sort', sort);

    // Gå direkt till PocketBase (cross-origin), samma som watchlist.
    const url = `${pbBaseUrl}/api/collections/stocks/records?${params.toString()}`;

    // Avbryt föregående in-flight request om state ändras snabbt.
    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          // Läs PocketBases felmeddelande istället för att gissa.
          const text = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
        }
        return res.json() as Promise<{ items: Stock[]; totalItems: number }>;
      })
      .then((data) => {
        if (cancelled || !data) return;
        setStocks(data.items);
        setTotal(data.totalItems);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        // Ignorera förväntade avbrott från oss själva (AbortController).
        if (e?.name === 'AbortError') return;
        setError(e?.message ?? String(e));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        firstRender.current = false;
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debouncedKey]);

  // Skriv state -> URL. Hoppar över default-sortering och page=1 för snygga URL:er.
  const updateState = (next: ScreenerState) => {
    setSearchParams(stateToSearchParams(next), { replace: false });
  };
  const updateSort = (sort: ScreenerState['sort']) => updateState({ ...state, sort, page: 1 });
  const reset = () => setSearchParams(stateToSearchParams(SCREENER_DEFAULTS));

  const totalPages = Math.max(1, Math.ceil(total / state.perPage));

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Screener</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {loading ? 'Laddar…' : `${total} träffar`}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside>
          <FilterPanel state={state} onChange={updateState} onReset={reset} />
        </aside>

        <section>
          {error && (
            <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              Kunde inte hämta aktier: {error}
            </div>
          )}

          <ScreenerTable
            stocks={stocks}
            loading={loading}
            sort={state.sort}
            onSortChange={updateSort}
          />

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                type="button"
                disabled={state.page <= 1}
                onClick={() => updateState({ ...state, page: state.page - 1 })}
                className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                ← Föregående
              </button>
              <span className="text-slate-600 dark:text-slate-400">
                Sida {state.page} av {totalPages}
              </span>
              <button
                type="button"
                disabled={state.page >= totalPages}
                onClick={() => updateState({ ...state, page: state.page + 1 })}
                className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Nästa →
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function useDebouncedKey(key: string, ms: number): string {
  const [v, setV] = useState(key);
  useEffect(() => {
    const t = setTimeout(() => setV(key), ms);
    return () => clearTimeout(t);
  }, [key, ms]);
  return v;
}
